import React, { useState, useEffect } from 'react';
import DashboardModal from '@/components/dashboard-modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import ActionButton from '@/components/ui/ActionButton';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  WandSparkles, 
  Sparkles, 
  Edit3, 
  BookOpen,
  Zap,
  FileText,
  List,
  Hash,
  Type,
  AlignLeft,
  FileStack,
  CheckCircle,
  Volume2,
  Palette,
  ArrowRight,
  ChevronDown,
  AlertCircle,
  Loader2,
  Microscope
} from 'lucide-react';
import WritingStyleDropdown from '@/components/content-generator/editor/WritingStyleDropdown';
import { StandardSelect, SelectOption } from '@/components/ui/form-fields/StandardSelect';
import { cn } from '@/lib/utils';
import { Accordion, AccordionItem, AccordionContent } from '@/components/ui/accordion';
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { AttachedResearch } from '@/hooks/content-generator/use-research-attachments';
import MarkdownTableParser from '@/components/content-generator/research-summary/MarkdownTableParser';
import { validateMarkdownContent, extractTablePreview } from '@/components/content-generator/research-summary/utils';
import { useAITextGeneration } from '@/hooks/ai-text-generation';
import { usePageContentDetailQuery } from '@/hooks/content-generator/pages/use-page-content-hooks';
import { useIsMobile } from '@/hooks/layout/use-mobile';

interface AITextGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTextGenerated: (text: string, mode?: 'insert' | 'replace') => void;
  selectedText: string;
  hasSelection: boolean;
  workbookId?: string;
  pageId?: string;
  currentWritingStyle?: string;
  pageName?: string;
  threadId?: string;
  pageResearch?: AttachedResearch[];
  workbookResearch?: AttachedResearch[];
  researchSummaries?: AttachedResearch[];
}

// 🎯 NEW: Tab-specific state interface for independent workflows
interface TabState {
  generatedText: string;
  isGenerating: boolean;
  hasGenerated: boolean;
  lastGenerated?: Date;
}

// Custom AccordionTrigger for research items (reused from ResearchSummaryContainer)
const ResearchAccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    attachment: AttachedResearch;
  }
>(({ className, children, attachment, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-3 px-3 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180 text-left hover:no-underline hover:bg-white/10 dark:hover:bg-white/5 transition-all duration-200 min-w-0",
        className
      )}
      {...props}
    >
      <div className="flex-1 min-w-0 pr-2">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
          {attachment.research_library.title}
        </h4>
      </div>
      
      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
ResearchAccordionTrigger.displayName = "ResearchAccordionTrigger";

const AITextGenerationModal: React.FC<AITextGenerationModalProps> = ({
  isOpen,
  onClose,
  onTextGenerated,
  selectedText,
  hasSelection,
  workbookId,
  pageId,
  currentWritingStyle,
  pageName = '',
  threadId = '',
  pageResearch = [],
  workbookResearch = [],
  researchSummaries = []
}) => {
  const { isMobile } = useIsMobile();
  const [activeTab, setActiveTab] = useState<string>('generate');
  const [prompt, setPrompt] = useState('');
  const [contentType, setContentType] = useState('paragraph');
  const [paragraphCount, setParagraphCount] = useState([2]);
  
  // 🎯 NEW: Tab-specific state objects for independent workflows
  const [enhanceTabState, setEnhanceTabState] = useState<TabState>({
    generatedText: '',
    isGenerating: false,
    hasGenerated: false
  });
  
  const [generateTabState, setGenerateTabState] = useState<TabState>({
    generatedText: '',
    isGenerating: false,
    hasGenerated: false
  });
  
  // Enhance tab state
  const [enhanceOption, setEnhanceOption] = useState('expand');
  const [enhanceInstructions, setEnhanceInstructions] = useState('');
  
  // Research accordion state
  const [activeResearchAccordion, setActiveResearchAccordion] = useState<string>('');
  
  // Page research selection state
  const [selectedPageResearch, setSelectedPageResearch] = useState<Set<string>>(
    new Set(pageResearch.map(r => r.id)) // Initially select all page research
  );
  
  // Workbook research selection state
  const [selectedWorkbookResearch, setSelectedWorkbookResearch] = useState<Set<string>>(
    new Set(workbookResearch.map(r => r.id)) // Initially select all workbook research
  );

  const { generateText } = useAITextGeneration();

  // Intelligently set the active tab based on whether text is selected
  useEffect(() => {
    if (isOpen) {
      // 🎯 SMART TAB PRIORITIZATION: Edit Content tab when text is selected
      if (hasSelection) {
        setActiveTab('enhance'); // Prioritize "Edit Content" when text is selected
      } else {
        setActiveTab('generate'); // Default to "Custom Instructions" when no text selected
      }
    }
  }, [isOpen, hasSelection]);

  // Helper functions for page research selection
  const togglePageResearch = (researchId: string) => {
    setSelectedPageResearch(prev => {
      const newSet = new Set(prev);
      if (newSet.has(researchId)) {
        newSet.delete(researchId);
      } else {
        newSet.add(researchId);
      }
      return newSet;
    });
  };

  const selectAllPageResearch = () => {
    setSelectedPageResearch(new Set(pageResearch.map(r => r.id)));
  };

  const deselectAllPageResearch = () => {
    setSelectedPageResearch(new Set());
  };

  // Helper functions for workbook research selection
  const toggleWorkbookResearch = (researchId: string) => {
    setSelectedWorkbookResearch(prev => {
      const newSet = new Set(prev);
      if (newSet.has(researchId)) {
        newSet.delete(researchId);
      } else {
        newSet.add(researchId);
      }
      return newSet;
    });
  };

  const selectAllWorkbookResearch = () => {
    setSelectedWorkbookResearch(new Set(workbookResearch.map(r => r.id)));
  };

  const deselectAllWorkbookResearch = () => {
    setSelectedWorkbookResearch(new Set());
  };

  // Generate text function
  const handleGenerate = async () => {
    setGenerateTabState(prev => ({ ...prev, isGenerating: true }));
    try {
      // Filter research to only include selected items
      const selectedPageResearchItems = (pageResearch || []).filter(r => 
        selectedPageResearch.has(r.id)
      );
      const selectedWorkbookResearchItems = (workbookResearch || []).filter(r => 
        selectedWorkbookResearch.has(r.id)
      );

      // Use edit mode when we have selected text, otherwise generate mode
      const mode = hasSelection ? 'edit' : 'generate';

      const content = await generateText({
        thread_id: threadId,
        mode: mode,
        prompt: prompt,
        selectedText: hasSelection ? selectedText : undefined,
        contentType: contentType,
        contentLength: paragraphCount[0],
        context: {
          pageTitle: pageName,
          workbookTitle: workbookId ? 'Workbook Content' : undefined,
          writingStyle: currentWritingStyle,
          researchSummaries: [
            ...selectedPageResearchItems.map(r => ({
              title: r.research_library.title,
              summary: r.research_summary || r.research_library.content?.substring(0, 200) || ''
            })),
            ...selectedWorkbookResearchItems.map(r => ({
              title: r.research_library.title,
              summary: r.research_summary || r.research_library.content?.substring(0, 200) || ''
            }))
          ]
        }
      });
      setGenerateTabState(prev => ({ ...prev, generatedText: content, isGenerating: false, hasGenerated: true, lastGenerated: new Date() }));
    } catch (error) {
      console.error('Generation failed:', error);
    }
  };

  const handleGenerateAndInsert = async () => {
    const currentTabState = activeTab === 'generate' ? generateTabState : enhanceTabState;
    
    if (!currentTabState.generatedText) {
      // Different behavior based on active tab
      if (activeTab === 'generate') {
        // Generate using real AI (matching enhance pattern)
        await handleGenerate();
        // After generation, wait for state update then insert
        setTimeout(() => {
          if (generateTabState.generatedText) {
            onTextGenerated(generateTabState.generatedText, hasSelection ? 'replace' : 'insert');
          }
        }, 2100); // Same delay as enhance/edit
              } else if (activeTab === 'enhance') {
          // Enhance selected text
          await handleEnhance();
          // After enhancement, the text will be in enhanceTabState.generatedText but we need to insert it
          setTimeout(() => {
            if (enhanceTabState.generatedText) {
              onTextGenerated(enhanceTabState.generatedText, 'replace');
            }
          }, 2100); // Slight delay to ensure state is updated
        }
    } else {
      // Insert existing text from current tab
      onTextGenerated(currentTabState.generatedText, hasSelection ? 'replace' : 'insert');
    }
  };

  // Content type options with icons
  const contentTypeOptions: SelectOption[] = [
    { value: 'paragraph', label: 'Paragraph' },
    { value: 'introduction', label: 'Introduction' },
    { value: 'conclusion', label: 'Conclusion' },
    { value: 'list', label: 'List' },
    { value: 'heading', label: 'Heading' },
    { value: 'summary', label: 'Summary' }
  ];

  // Get icon for content type
  const getContentTypeIcon = () => {
    switch (contentType) {
      case 'paragraph': return <AlignLeft size={16} />;
      case 'introduction': return <FileText size={16} />;
      case 'conclusion': return <FileStack size={16} />;
      case 'list': return <List size={16} />;
      case 'heading': return <Hash size={16} />;
      case 'summary': return <Type size={16} />;
      default: return <FileText size={16} />;
    }
  };

  // Get action word for each enhancement option
  const getActionWord = (optionId: string): string => {
    switch (optionId) {
      case 'expand': return 'elaborate';
      case 'improve': return 'polish';
      case 'engaging': return 'captivate';
      case 'grammar': return 'correct';
      // REMOVED: case 'style': return 'refine';
      case 'clarity': return 'simplify';
      default: return 'enhance';
    }
  };

  // Enhancement options (includes both enhance and edit operations)
  // Ordered by frequency of use: Quick fixes → Quality improvements → Content expansion
  const enhanceOptions = [
    { 
      id: 'grammar', 
      name: 'Fix Grammar & Spelling', 
      icon: CheckCircle,
      description: 'Correct grammatical errors, typos, and spelling mistakes'
    },
    { 
      id: 'clarity', 
      name: 'Increase Clarity', 
      icon: ArrowRight,
      description: 'Make the text clearer and easier to understand'
    },
    { 
      id: 'improve', 
      name: 'Improve Writing', 
      icon: Edit3,
      description: 'Enhance clarity, flow, and readability'
    },
    // REMOVED: Improve Style option
    // { 
    //   id: 'style', 
    //   name: 'Improve Style', 
    //   icon: Palette,
    //   description: 'Enhance sentence structure, word choice, and flow'
    // },
    { 
      id: 'engaging', 
      name: 'Make Engaging', 
      icon: Zap,
      description: 'Add personality, hooks, and compelling language'
    },
    { 
      id: 'expand', 
      name: 'Expand & Add Details', 
      icon: Sparkles,
      description: 'Add more depth, examples, and supporting details'
    }
  ];

  // Enhance text function
  const handleEnhance = async () => {
    if (!selectedText.trim()) return;
    
    setEnhanceTabState(prev => ({ ...prev, isGenerating: true }));
    try {
      // Filter research to only include selected items
      const selectedPageResearchItems = (pageResearch || []).filter(r => 
        selectedPageResearch.has(r.id)
      );
      const selectedWorkbookResearchItems = (workbookResearch || []).filter(r => 
        selectedWorkbookResearch.has(r.id)
      );

      const enhancementType = enhanceOptions.find(opt => opt.id === enhanceOption)?.name || '';
      const instructions = enhanceInstructions || `Apply ${enhancementType}`;
      
      // Determine mode based on option type
      const isEditOperation = ['grammar', 'clarity'].includes(enhanceOption);
      const mode = isEditOperation ? 'edit' : 'enhance';
      
      const content = await generateText({
        thread_id: threadId,
        mode: mode,
        prompt: instructions,
        selectedText: selectedText,
        enhanceType: isEditOperation ? undefined : enhanceOption,
        editType: isEditOperation ? enhanceOption : undefined,
        context: {
          pageTitle: pageName,
          workbookTitle: workbookId ? 'Workbook Content' : undefined,
          writingStyle: currentWritingStyle,
          researchSummaries: [
            ...selectedPageResearchItems.map(r => ({
              title: r.research_library.title,
              summary: r.research_summary || r.research_library.content?.substring(0, 200) || ''
            })),
            ...selectedWorkbookResearchItems.map(r => ({
              title: r.research_library.title,
              summary: r.research_summary || r.research_library.content?.substring(0, 200) || ''
            }))
          ]
        }
      });
      setEnhanceTabState(prev => ({ ...prev, generatedText: content, isGenerating: false, hasGenerated: true, lastGenerated: new Date() }));
    } catch (error) {
      console.error('Enhancement failed:', error);
    }
  };

  // Calculate if user has unsaved changes
  const hasUnsavedChanges = !!(
    prompt.trim() || 
    generateTabState.generatedText ||
    enhanceTabState.generatedText ||
    selectedPageResearch.size > 0 || 
    selectedWorkbookResearch.size > 0 ||
    enhanceInstructions.trim()
  );

  // 🎯 Helper function to render preview section with current tab state
  const renderPreviewSection = () => {
    const currentTabState = activeTab === 'generate' ? generateTabState : enhanceTabState;
    
    if (!(hasSelection || currentTabState.generatedText || currentTabState.isGenerating)) {
      return null;
    }

    return (
      <div className="space-y-4">
        {/* Generated Text or Loading State */}
        {(currentTabState.generatedText || currentTabState.isGenerating) && (
          <div className="bg-white/70 dark:bg-gray-800/50 rounded-lg p-4 border border-purple-200/50 dark:border-purple-700/30">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-violet-500"></div>
              <span className="text-xs font-medium text-purple-700 dark:text-purple-300 uppercase tracking-wide">
                {activeTab === 'generate' ? (hasSelection ? 'Edited Content' : 'New Content') : 'Enhanced'}
              </span>
            </div>
            
            {currentTabState.isGenerating ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 text-purple-500 animate-spin mx-auto mb-3" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    Generating content...
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    This may take a few seconds
                  </p>
                </div>
              </div>
            ) : (
              <>
                <style dangerouslySetInnerHTML={{
                  __html: `
                    .ai-content-preview h1 {
                      font-size: 16px !important;
                      font-weight: 700 !important;
                      margin: 8px 0 4px 0 !important;
                      color: #1f2937 !important;
                    }
                    .ai-content-preview h2 {
                      font-size: 15px !important;
                      font-weight: 600 !important;
                      margin: 6px 0 3px 0 !important;
                      color: #374151 !important;
                    }
                    .ai-content-preview h3 {
                      font-size: 14px !important;
                      font-weight: 600 !important;
                      margin: 4px 0 2px 0 !important;
                      color: #374151 !important;
                    }
                    .ai-content-preview p {
                      margin: 6px 0 !important;
                      color: #4B5563 !important;
                      line-height: 1.6 !important;
                    }
                    .ai-content-preview strong {
                      font-weight: 600 !important;
                      color: #1f2937 !important;
                    }
                    .ai-content-preview em {
                      font-style: italic !important;
                      color: #6B7280 !important;
                    }
                    .ai-content-preview ul, .ai-content-preview ol {
                      margin: 6px 0 !important;
                      padding-left: 18px !important;
                    }
                    .ai-content-preview li {
                      margin: 3px 0 !important;
                      color: #4B5563 !important;
                    }
                    .ai-content-preview code {
                      background: #f3f4f6 !important;
                      padding: 2px 6px !important;
                      border-radius: 4px !important;
                      font-size: 12px !important;
                      color: #dc2626 !important;
                      font-family: 'SF Mono', Monaco, monospace !important;
                    }
                    .ai-content-preview blockquote {
                      border-left: 3px solid #a855f7 !important;
                      padding-left: 12px !important;
                      margin: 8px 0 !important;
                      color: #6B7280 !important;
                      font-style: italic !important;
                      background: #faf5ff !important;
                      border-radius: 4px !important;
                      padding: 8px 12px !important;
                    }
                    .dark .ai-content-preview h1,
                    .dark .ai-content-preview h2,
                    .dark .ai-content-preview h3 {
                      color: #f3f4f6 !important;
                    }
                    .dark .ai-content-preview p,
                    .dark .ai-content-preview li {
                      color: #d1d5db !important;
                    }
                    .dark .ai-content-preview strong {
                      color: #f9fafb !important;
                    }
                    .dark .ai-content-preview em {
                      color: #9ca3af !important;
                    }
                    .dark .ai-content-preview code {
                      background: #374151 !important;
                      color: #f87171 !important;
                    }
                    .dark .ai-content-preview blockquote {
                      border-left-color: #8b5cf6 !important;
                      color: #9ca3af !important;
                      background: #1e1b4b !important;
                    }
                  `
                }} />
                
                <div className="text-sm text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700/50 rounded-md p-3 border border-gray-200 dark:border-gray-600">
                  <div 
                    className="ai-content-preview leading-relaxed"
                    dangerouslySetInnerHTML={{ 
                      __html: currentTabState.generatedText
                        // Convert basic markdown to HTML for preview
                        .replace(/^### (.*$)/gm, '<h3>$1</h3>')
                        .replace(/^## (.*$)/gm, '<h2>$1</h2>')
                        .replace(/^# (.*$)/gm, '<h1>$1</h1>')
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        .replace(/`([^`]+)`/g, '<code>$1</code>')
                        .replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>')
                        .replace(/^\* (.*$)/gm, '<li>$1</li>')
                        .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
                        .replace(/^\d+\. (.*$)/gm, '<li>$1</li>')
                        .replace(/\n\n/g, '</p><p>')
                        .replace(/^(.)/gm, '<p>$1')
                        .replace(/(.)$/gm, '$1</p>')
                        .replace(/<\/p><p><h/g, '</p><h')
                        .replace(/<\/h([1-6])><p>/g, '</h$1><p>')
                        .replace(/<\/p><p><ul>/g, '</p><ul>')
                        .replace(/<\/ul><p>/g, '</ul><p>')
                        .replace(/<\/p><p><blockquote>/g, '</p><blockquote>')
                        .replace(/<\/blockquote><p>/g, '</blockquote><p>')
                        .replace(/<p><\/p>/g, '')
                    }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Selected Text Display */}
        {hasSelection && (
          <div className="bg-white/70 dark:bg-gray-800/50 rounded-lg p-4 border border-orange-200/50 dark:border-orange-700/30">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-500"></div>
              <span className="text-xs font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wide">Original Text</span>
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700/50 rounded-md p-3 border border-gray-200 dark:border-gray-600">
              {selectedText}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardModal
      isOpen={isOpen}
      onClose={onClose}
      title="✨ AI Text Magic"
      accentColor="purple"
      size="lg"
      hasUnsavedChanges={hasUnsavedChanges}
      preventCloseOnUnsavedChanges={true}
      onDiscardChanges={onClose}
      onSaveChanges={handleGenerateAndInsert}
    >
      <div className="space-y-6">

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-gray-100 dark:bg-gray-800">
            <TabsTrigger value="enhance" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              <span className="hidden sm:inline">Edit Content</span>
              <span className="sm:hidden">Edit</span>
            </TabsTrigger>
            <TabsTrigger value="generate" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">Custom Instructions</span>
              <span className="sm:hidden">Custom</span>
            </TabsTrigger>
          </TabsList>

          {/* Generate Tab */}
          <TabsContent value="generate" className="space-y-6 mt-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="prompt" className="text-sm font-medium mb-2 block">
                  {hasSelection 
                    ? "How would you like us to edit your selected text?" 
                    : "What new content would you like to generate?"
                  }
                </Label>
                <Textarea
                  id="prompt"
                  placeholder={hasSelection 
                    ? "Explain how you want us to modify the selected text... (e.g., 'Make this more professional and concise' or 'Add more technical details and examples' or 'Rewrite this in a friendlier tone')"
                    : "Describe what you want to create... (e.g., 'Write a comprehensive guide on remote work productivity tips for teams' or 'Create a technical overview of blockchain technology for beginners')"
                  }
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  variant="purple"
                  className="min-h-[100px] resize-none"
                />
              </div>

              {/* Content Type */}
              <div>
                <StandardSelect
                  id="content-type"
                  label="Content Type"
                  icon={getContentTypeIcon()}
                  colorVariant="purple"
                  value={contentType}
                  onChange={(value) => setContentType(Array.isArray(value) ? value[0] || 'paragraph' : value)}
                  options={contentTypeOptions}
                  placeholder="Select content type"
                />
              </div>

              {/* Paragraph Count Slider - Modern Design */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Content Length
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 rounded-full border border-purple-200 dark:border-purple-800">
                      <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">
                        {paragraphCount[0]} paragraph{paragraphCount[0] !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="relative">
                  <Slider
                    value={paragraphCount}
                    onValueChange={setParagraphCount}
                    max={14}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  
                  {/* Custom paragraph count indicators */}
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2 px-1">
                    <div className="flex flex-col items-center">
                      <div className={cn(
                        "w-2 h-2 rounded-full mb-1 transition-colors",
                        paragraphCount[0] <= 2 ? "bg-purple-500" : "bg-gray-300 dark:bg-gray-600"
                      )} />
                      <span className={cn(
                        "font-medium transition-colors",
                        paragraphCount[0] <= 2 ? "text-purple-600 dark:text-purple-400" : ""
                      )}>
                        Short
                      </span>
                      <span className="text-xs text-gray-400 mt-0.5">1-2</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className={cn(
                        "w-2 h-2 rounded-full mb-1 transition-colors",
                        paragraphCount[0] > 2 && paragraphCount[0] <= 5 ? "bg-purple-500" : "bg-gray-300 dark:bg-gray-600"
                      )} />
                      <span className={cn(
                        "font-medium transition-colors",
                        paragraphCount[0] > 2 && paragraphCount[0] <= 5 ? "text-purple-600 dark:text-purple-400" : ""
                      )}>
                        Medium
                      </span>
                      <span className="text-xs text-gray-400 mt-0.5">3-5</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className={cn(
                        "w-2 h-2 rounded-full mb-1 transition-colors",
                        paragraphCount[0] > 5 ? "bg-purple-500" : "bg-gray-300 dark:bg-gray-600"
                      )} />
                      <span className={cn(
                        "font-medium transition-colors",
                        paragraphCount[0] > 5 ? "text-purple-600 dark:text-purple-400" : ""
                      )}>
                        Long
                      </span>
                      <span className="text-xs text-gray-400 mt-0.5">6-14</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </TabsContent>

          {/* Enhance Tab */}
          <TabsContent value="enhance" className="space-y-6 mt-6">
            {!hasSelection ? (
              <div className="text-center py-8 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <Edit3 className="h-12 w-12 text-purple-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-purple-700 dark:text-purple-300">Select Text to Edit</h3>
                <p className="text-purple-600 dark:text-purple-400">
                  Please select some text in your editor first, then open this modal to edit it.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Enhancement Type Selector */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">How would you like to edit your text?</Label>
                  <div className="grid grid-cols-1 gap-3">
                    {enhanceOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setEnhanceOption(option.id)}
                        className={cn(
                          "p-3 rounded-lg border text-left transition-all duration-200 flex items-center justify-between gap-3",
                          enhanceOption === option.id
                            ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-sm"
                            : "border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <option.icon className={cn(
                            "h-5 w-5 flex-shrink-0",
                            enhanceOption === option.id ? "text-purple-600 dark:text-purple-400" : "text-gray-500"
                          )} />
                          <span className={cn(
                            "font-medium text-sm",
                            enhanceOption === option.id ? "text-purple-700 dark:text-purple-300" : "text-gray-700 dark:text-gray-300"
                          )}>
                            {option.name}
                          </span>
                        </div>
                        <span className={cn(
                          "text-xs italic font-normal",
                          enhanceOption === option.id ? "text-purple-500/70" : "text-purple-400/50"
                        )}>
                          → {getActionWord(option.id)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                
              </div>
            )}
          </TabsContent>

        </Tabs>

        {/* Content Preview Section - Modern Design with Loading States */}
        {renderPreviewSection()}

        {/* TEMPORARILY HIDDEN: Research Context Section - Can be re-enabled by changing false to the condition below */}
        {false && ((pageResearch?.length > 0) || (workbookResearch?.length > 0)) && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 space-y-4">
          {/* Research Instructions */}
          <div className="bg-blue-50/50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200/50 dark:border-blue-700/50">
            <p className="text-sm text-blue-700 dark:text-blue-300 font-medium mb-1">
              📚 Research Context
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Select research below to provide AI with relevant context. Selected research will be used to inform and enhance the generated content.
            </p>
          </div>

          {/* Hidden Page Title and Thread Data for Backend Access */}
          {pageName && (
            <span className="sr-only" data-page-title={pageName}>
              Page: {pageName}
            </span>
          )}
          {threadId && (
            <span className="sr-only" data-thread-id={threadId}>
              Thread: {threadId}
            </span>
          )}

          {/* Document Research - Interactive Selection */}
          {pageResearch.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  Document Research ({selectedPageResearch.size}/{pageResearch.length})
                </Label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={selectAllPageResearch}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                    disabled={selectedPageResearch.size === pageResearch.length}
                  >
                    Select All
                  </button>
                  <span className="text-xs text-gray-400">|</span>
                  <button
                    onClick={deselectAllPageResearch}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                    disabled={selectedPageResearch.size === 0}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {pageResearch.map(attachment => (
                  <div
                    key={attachment.id}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md border transition-all duration-200 cursor-pointer",
                      selectedPageResearch.has(attachment.id)
                        ? "bg-blue-50/70 dark:bg-blue-900/20 border-blue-200 dark:border-blue-600"
                        : "bg-gray-50/70 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-600"
                    )}
                    onClick={() => togglePageResearch(attachment.id)}
                    title={`Document Research: ${attachment.research_library.title}`}
                  >
                    <Checkbox
                      checked={selectedPageResearch.has(attachment.id)}
                      onChange={() => togglePageResearch(attachment.id)}
                      variant="blue"
                      className="flex-shrink-0"
                    />
                    <Microscope size={14} className={cn(
                      "flex-shrink-0",
                      selectedPageResearch.has(attachment.id)
                        ? "text-blue-600 dark:text-blue-400"
                        : "text-gray-500 dark:text-gray-400"
                    )} />
                    <span className={cn(
                      "text-sm truncate",
                      selectedPageResearch.has(attachment.id)
                        ? "text-gray-700 dark:text-gray-300"
                        : "text-gray-600 dark:text-gray-400"
                    )}>
                      {attachment.research_library.title}
                    </span>
                    {/* Hidden data for backend access */}
                    <span className="sr-only" data-research-id={attachment.id} data-research-summary={attachment.research_summary}>
                      {attachment.research_summary}
                    </span>
                  </div>
                ))}
              </div>
              {selectedPageResearch.size === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  No document research selected. Select research to enhance AI generation with document-specific context.
                </p>
              )}
            </div>
          )}

          {/* Workbook Research - Interactive Selection */}
          {workbookResearch.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-purple-700 dark:text-purple-300">
                  Workbook Research ({selectedWorkbookResearch.size}/{workbookResearch.length})
                </Label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={selectAllWorkbookResearch}
                    className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
                    disabled={selectedWorkbookResearch.size === workbookResearch.length}
                  >
                    Select All
                  </button>
                  <span className="text-xs text-gray-400">|</span>
                  <button
                    onClick={deselectAllWorkbookResearch}
                    className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
                    disabled={selectedWorkbookResearch.size === 0}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {workbookResearch.map(attachment => (
                  <div
                    key={attachment.id}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md border transition-all duration-200 cursor-pointer",
                      selectedWorkbookResearch.has(attachment.id)
                        ? "bg-purple-50/70 dark:bg-purple-900/20 border-purple-200 dark:border-purple-600"
                        : "bg-gray-50/70 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:border-purple-200 dark:hover:border-purple-600"
                    )}
                    onClick={() => toggleWorkbookResearch(attachment.id)}
                    title={`Workbook Research: ${attachment.research_library.title}`}
                  >
                    <Checkbox
                      checked={selectedWorkbookResearch.has(attachment.id)}
                      onChange={() => toggleWorkbookResearch(attachment.id)}
                      variant="purple"
                      className="flex-shrink-0"
                    />
                    <Microscope size={14} className={cn(
                      "flex-shrink-0",
                      selectedWorkbookResearch.has(attachment.id)
                        ? "text-purple-600 dark:text-purple-400"
                        : "text-gray-500 dark:text-gray-400"
                    )} />
                    <span className={cn(
                      "text-sm truncate",
                      selectedWorkbookResearch.has(attachment.id)
                        ? "text-gray-700 dark:text-gray-300"
                        : "text-gray-600 dark:text-gray-400"
                    )}>
                      {attachment.research_library.title}
                    </span>
                    {/* Hidden data for backend access */}
                    <span className="sr-only" data-research-id={attachment.id} data-research-summary={attachment.research_summary}>
                      {attachment.research_summary}
                    </span>
                  </div>
                ))}
              </div>
              {selectedWorkbookResearch.size === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  No workbook research selected. Select research to enhance AI generation with workbook context.
                </p>
              )}
            </div>
          )}
        </div>
        )}

        {/* Inline Footer Buttons */}
        <div className="w-full flex justify-between items-center pt-4">
          <ActionButton 
            variant="cancel" 
            onClick={onClose}
            disabled={generateTabState.isGenerating || enhanceTabState.isGenerating}
          >
            Cancel
          </ActionButton>
          
          <ActionButton 
            variant="purple" 
            icon={<WandSparkles size={16} />}
            onClick={handleGenerateAndInsert}
            disabled={
              (activeTab === 'generate' && !prompt.trim() && !generateTabState.generatedText) ||
              (activeTab === 'enhance' && !hasSelection && !enhanceTabState.generatedText) ||
              generateTabState.isGenerating ||
              enhanceTabState.isGenerating
            }
            className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
          >
            {(() => {
              const currentTabState = activeTab === 'generate' ? generateTabState : enhanceTabState;
              return currentTabState.generatedText ? (
              hasSelection ? 'Replace Text' : 'Insert Text'
            ) : hasSelection ? (
              activeTab === 'enhance' ? 'Enhance Text' : 'Edit Text'
            ) : (
              activeTab === 'enhance' ? 'Enhance Text' : 'Create Content'
              );
            })()}
          </ActionButton>
        </div>

      </div>
    </DashboardModal>
  );
};

export default AITextGenerationModal;
